import { useMutation, useQuery } from 'react-query';
import { AxiosError, AxiosResponse } from 'axios';
import { useBackdropContext } from '../../../../context/backdrop';
import { useErrors } from '../../../../hooks/errors/use-errors';
import { useNative } from '../../../../hooks/native/use-native';
import { useHttpClient, useRestClient } from '../../../../utils/clients/rest-client';
import {
  ConnectToNetworkRequest,
  ConnectToUnknownNetworkRequest,
  ConnectToUnknownNetworkRequestBatch,
  DeviceConnectToNetworkData,
  DeviceConnectToUnknownNetworkBatchData,
  DeviceConnectToUnknownNetworkData,
  DeviceGetWifiNetworksBatchData,
  DeviceGetWifiNetworksData,
  DeviceResetToDefaultsData,
  DeviceRestartData,
  DeviceSetActionBindingsData,
  DeviceSetActionBindingsError,
  DeviceSwapInData,
  DeviceSwapInError,
  DeviceUpdateFirmwareData,
  GenericDeviceRequest,
  GetWifiNetworksRequest,
  ResetToDefaultsRequest,
  RestartDeviceRequest,
  SwapInRequest,
} from '../../types/openapi/openapi-types';
import { DeviceBindingsBody } from './device.types';

export const useUpdateFirmware = () => {
  const httpClient = useHttpClient();
  const { handleRestErrors } = useErrors();

  return useMutation<AxiosResponse<DeviceUpdateFirmwareData>, AxiosError<void>, GenericDeviceRequest>(
    ['device-updateFirmware'],
    async (body) => await httpClient.device.deviceUpdateFirmware(body),
    {
      onError: (error) => {
        handleRestErrors(error);
      },
    },
  );
};

export const useDeviceSwapIn = () => {
  const httpClient = useHttpClient();
  const { handleRestErrors } = useErrors();
  const { fireVibration } = useNative();

  return useMutation<AxiosResponse<DeviceSwapInData>, AxiosError<DeviceSwapInError>, SwapInRequest>(
    ['device-swapIn'],
    async (body) => {
      fireVibration();
      return await httpClient.device.deviceSwapIn(body);
    },
    {
      onError: (error) => {
        handleRestErrors(error);
      },
    },
  );
};

export const useGetWiFiNetworks = () => {
  const httpClient = useHttpClient();
  const { handleRestErrors } = useErrors();

  return useMutation<AxiosResponse<DeviceGetWifiNetworksData>, AxiosError<void>, GetWifiNetworksRequest>(
    ['device-wifi-networks'],
    async (body) => await httpClient.device.deviceGetWifiNetworks(body),
    {
      onError: (error) => {
        handleRestErrors(error);
      },
    },
  );
};

export const useGetWiFiNetworksBatch = () => {
  const httpClient = useHttpClient();
  const { turnOnBackdrop, turnOffBackdrop } = useBackdropContext();
  const { handleRestErrors } = useErrors();

  return useQuery<DeviceGetWifiNetworksBatchData, AxiosError<void>>(
    ['device-wifi-networks-batch'],
    async () => {
      turnOnBackdrop();
      const response = await httpClient.device.deviceGetWifiNetworksBatch();
      return response.data;
    },
    {
      onSuccess: () => {
        turnOffBackdrop();
      },
      onError: (error) => {
        handleRestErrors(error);
      },
      cacheTime: 0,
    },
  );
};

export const useConnectToNetwork = () => {
  const httpClient = useHttpClient();
  const { handleRestErrors } = useErrors();

  return useMutation<AxiosResponse<DeviceConnectToNetworkData>, AxiosError<void>, ConnectToNetworkRequest>(
    ['device-connect-to-network'],
    async (body) => await httpClient.device.deviceConnectToNetwork(body),
    {
      onError: (error) => {
        handleRestErrors(error);
      },
    },
  );
};

export const useConnectToUnknownNetwork = () => {
  const httpClient = useHttpClient();
  const { handleRestErrors } = useErrors();

  return useMutation<
    AxiosResponse<DeviceConnectToUnknownNetworkData>,
    AxiosError<void>,
    ConnectToUnknownNetworkRequest
  >(['device-connect-to-network'], async (body) => await httpClient.device.deviceConnectToUnknownNetwork(body), {
    onError: (error) => {
      handleRestErrors(error);
    },
  });
};

export const useConnectToUnknownNetworkBatch = () => {
  const httpClient = useHttpClient();
  const { handleRestErrors } = useErrors();

  return useMutation<
    AxiosResponse<DeviceConnectToUnknownNetworkBatchData>,
    AxiosError<void>,
    ConnectToUnknownNetworkRequestBatch
  >(
    ['device-connect-to-network-batch'],
    async (body) => await httpClient.device.deviceConnectToUnknownNetworkBatch(body),
    {
      onError: (error) => {
        handleRestErrors(error);
      },
    },
  );
};

export const useDeviceRestart = () => {
  const httpClient = useHttpClient();
  const { handleRestErrors } = useErrors();

  return useMutation<AxiosResponse<DeviceRestartData>, AxiosError<void>, RestartDeviceRequest>(
    ['device-restart'],
    async (body) => await httpClient.device.deviceRestart(body),
    {
      onError: (error) => {
        handleRestErrors(error);
      },
    },
  );
};

export const useSetActionBindings = () => {
  const restClient = useRestClient();

  return useMutation<
    AxiosResponse<DeviceSetActionBindingsData>,
    AxiosError<DeviceSetActionBindingsError>,
    DeviceBindingsBody
  >(
    ['device-setActionBindings'],
    async (body) => await restClient.post<DeviceSetActionBindingsData>('/device/setActionBindings', body),
  );
};

export const useDeviceResetToDefaults = () => {
  const httpClient = useHttpClient();
  const { handleRestErrors } = useErrors();

  return useMutation<AxiosResponse<DeviceResetToDefaultsData>, AxiosError<void>, ResetToDefaultsRequest>(
    ['device-resetToDefaults'],
    async (body) => await httpClient.device.deviceResetToDefaults(body),
    {
      onError: (error) => {
        handleRestErrors(error);
      },
    },
  );
};
